import React, { useEffect, useState } from "react";

/**
 * KCIS External Health Probe Page (`/healthz` on the SPA route table).
 *
 * Why this exists:
 * The Kubernetes ingress serves every non-`/api` path from the frontend
 * bundle. That means external HTTP monitors hitting `https://<domain>/healthz`
 * were receiving `200 OK` + SPA HTML regardless of backend status — a
 * silent monitoring blind spot.
 *
 * This page performs a live `fetch(/api/health)` and renders the actual
 * backend health signal, plus sets `document.title` for monitors that
 * inspect the title attribute.
 *
 * IMPORTANT: The SPA is always served with HTTP 200. Automated monitors
 * that rely solely on HTTP status code MUST use `/api/health` directly
 * (that path routes to the backend through the ingress and returns real
 * HTTP status codes). This page is for:
 *   1. Human eyeball checks
 *   2. Monitors that do content-string matching ("ok" / "DOWN")
 *   3. Browser-based synthetic monitors (Datadog Browser, Checkly, etc.)
 */
export default function HealthZ() {
  const [state, setState] = useState({ status: "checking", data: null, error: null });

  useEffect(() => {
    document.title = "KCIS · health check (checking…)";
    const API = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${API}/api/health`, { cache: "no-store" })
      .then(async (r) => {
        const text = await r.text();
        let body = null;
        try { body = JSON.parse(text); } catch (_) { body = text; }
        if (r.ok && body && (body.status === "ok" || (typeof body === "string" && body.includes("ok")))) {
          setState({ status: "ok", data: body, error: null });
          document.title = "KCIS · health OK";
        } else {
          setState({
            status: "down",
            data: body,
            error: `Backend returned HTTP ${r.status}`,
          });
          document.title = "KCIS · health DOWN";
        }
      })
      .catch((err) => {
        setState({ status: "down", data: null, error: err.message || String(err) });
        document.title = "KCIS · health DOWN";
      });
  }, []);

  const isOk = state.status === "ok";
  const bg = isOk ? "#e6f7ec" : state.status === "checking" ? "#f4f4f4" : "#fbe9e9";
  const fg = isOk ? "#065f46" : state.status === "checking" ? "#374151" : "#7f1d1d";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: bg,
        color: fg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        padding: 24,
      }}
      data-testid="healthz-page"
    >
      <div style={{ maxWidth: 640, width: "100%" }}>
        <div
          style={{ fontSize: 48, fontWeight: 700, letterSpacing: -1 }}
          data-testid="healthz-status"
        >
          {state.status === "checking" ? "checking…" : isOk ? "ok" : "DOWN"}
        </div>
        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.8 }}>
          service: kcis-backend · probed via /api/health
        </div>
        {state.error && (
          <pre
            style={{
              marginTop: 24,
              padding: 12,
              background: "rgba(0,0,0,0.06)",
              borderRadius: 8,
              fontSize: 12,
              overflow: "auto",
            }}
            data-testid="healthz-error"
          >
            {state.error}
          </pre>
        )}
        {state.data && (
          <pre
            style={{
              marginTop: 16,
              padding: 12,
              background: "rgba(0,0,0,0.06)",
              borderRadius: 8,
              fontSize: 12,
              overflow: "auto",
            }}
            data-testid="healthz-body"
          >
            {typeof state.data === "string" ? state.data : JSON.stringify(state.data, null, 2)}
          </pre>
        )}
        <div style={{ marginTop: 32, fontSize: 11, opacity: 0.65, lineHeight: 1.6 }}>
          Automated HTTP-status-based monitors must target{" "}
          <code>/api/health</code> directly — this SPA route always serves HTTP 200.
        </div>
      </div>
    </div>
  );
}
